import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, ShieldWarning, Fingerprint, ArrowsClockwise, Broadcast } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { odmHostKeysApi } from '../../services/api';

const STATUS_BADGE = {
  retrieved: { label: 'UNVERIFIED', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  verified_pinned: { label: 'VERIFIED & PINNED', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  change_detected: { label: 'KEY CHANGED — BLOCKED', cls: 'bg-red-100 text-red-800 border-red-300' },
  superseded: { label: 'SUPERSEDED', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
  rejected: { label: 'REJECTED', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
};

const FpRow = ({ label, value, testId }) => (
  <div className="flex items-start gap-2 text-xs">
    <span className="text-slate-500 w-28 shrink-0">{label}</span>
    <span className="font-mono break-all text-slate-800" data-testid={testId}>{value || '—'}</span>
  </div>
);

export const HostKeyPinningCard = ({ environment = 'cert' }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState('');
  const [fpInput, setFpInput] = useState('');
  const [sourceNote, setSourceNote] = useState('');
  const [preflight, setPreflight] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await odmHostKeysApi.status(environment);
      setStatus(r.data);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error('Failed to load host-key status');
    }
  }, [environment]);

  useEffect(() => { load(); }, [load]);

  const latest = status?.records?.[0] || null;
  const pinned = status?.pinned || null;
  const changeDetected = status?.records?.some((r) => r.status === 'change_detected');

  const handleCapture = async () => {
    setBusy('capture');
    try {
      await odmHostKeysApi.capture(environment);
      toast.success('Host key retrieved — UNVERIFIED until you confirm the authoritative fingerprint');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Capture failed (endpoint may require the tunnel IP whitelist)');
    } finally { setBusy(''); }
  };

  const handleVerify = async () => {
    if (!latest) return;
    setBusy('verify');
    try {
      await odmHostKeysApi.verify(environment, {
        record_id: latest.record_id,
        authoritative_fingerprint: fpInput,
        verification_source_note: sourceNote,
      });
      toast.success('Fingerprint verified — host key PINNED');
      setFpInput(''); setSourceNote('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification refused');
    } finally { setBusy(''); }
  };

  const handlePreflight = async () => {
    setBusy('preflight');
    try {
      const r = await odmHostKeysApi.preflight(environment);
      setPreflight(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preflight failed');
    } finally { setBusy(''); }
  };

  return (
    <div className="p-5 rounded-xl border-2 border-slate-200 bg-white space-y-4" data-testid={`host-key-card-${environment}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Fingerprint size={20} className="text-slate-700" />
          <h3 className="font-semibold text-slate-900">ODM Host Key Security — {environment.toUpperCase()}</h3>
        </div>
        {pinned
          ? <Badge className={STATUS_BADGE.verified_pinned.cls} data-testid="host-key-pin-state">PINNED</Badge>
          : <Badge className={STATUS_BADGE.retrieved.cls} data-testid="host-key-pin-state">NOT PINNED</Badge>}
      </div>

      {changeDetected && (
        <div className="p-3 rounded-lg border-2 border-red-300 bg-red-50 flex items-start gap-2" data-testid="host-key-change-warning">
          <ShieldWarning size={20} className="text-red-700 shrink-0" weight="fill" />
          <div className="text-xs text-red-800">
            <b>SECURITY WARNING:</b> the server presented a DIFFERENT host key than the pinned fingerprint.
            Live SFTP is blocked. Contact ODM/OMES, independently re-verify the new fingerprint, then re-pin.
            The trusted key is never replaced automatically.
          </div>
        </div>
      )}

      {latest ? (
        <div className="space-y-1.5 p-3 rounded-lg bg-slate-50 border border-slate-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-700">Latest retrieved key</span>
            <Badge className={(STATUS_BADGE[latest.status] || STATUS_BADGE.retrieved).cls} data-testid="host-key-record-status">
              {(STATUS_BADGE[latest.status] || STATUS_BADGE.retrieved).label}
            </Badge>
          </div>
          <FpRow label="Hostname" value={`${latest.hostname}:${latest.port}`} testId="host-key-hostname" />
          <FpRow label="Algorithm" value={latest.key_algorithm} testId="host-key-algorithm" />
          <FpRow label="SHA-256" value={latest.fingerprint_sha256} testId="host-key-fingerprint" />
          <FpRow label="Retrieved" value={`${latest.retrieved_at} (${latest.retrieval_path})`} testId="host-key-retrieved-at" />
          <FpRow label="Environment" value={latest.environment} testId="host-key-environment" />
          {latest.verified_at && <FpRow label="Verified" value={`${latest.verified_at} by ${latest.verified_by}`} testId="host-key-verified-at" />}
        </div>
      ) : (
        <p className="text-xs text-slate-500">No key retrieved yet. Capture presents the key the remote server offers — retrieval alone never establishes trust.</p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={handleCapture} disabled={busy !== ''} data-testid="host-key-capture-btn">
          <ArrowsClockwise size={14} className="mr-1" /> {busy === 'capture' ? 'Retrieving…' : 'Capture host key'}
        </Button>
        <Button size="sm" variant="outline" onClick={handlePreflight} disabled={busy !== ''} data-testid="host-key-preflight-btn">
          <Broadcast size={14} className="mr-1" /> {busy === 'preflight' ? 'Running…' : 'Connectivity preflight'}
        </Button>
      </div>

      {latest && ['retrieved', 'change_detected'].includes(latest.status) && (
        <div className="space-y-2 p-3 rounded-lg border border-amber-200 bg-amber-50">
          <div className="text-xs font-semibold text-amber-900 flex items-center gap-1">
            <ShieldCheck size={14} /> Independent verification required (no trust-on-first-use)
          </div>
          <p className="text-[11px] text-amber-800">
            Enter the authoritative SHA-256 fingerprint you obtained directly from ODM/OMES
            (email, portal or phone). Pinning only proceeds on an EXACT match.
          </p>
          <input
            className="w-full text-xs font-mono border rounded-md px-2 py-1.5 bg-white"
            placeholder="SHA256:…"
            value={fpInput}
            onChange={(e) => setFpInput(e.target.value)}
            data-testid="host-key-authoritative-fp-input"
          />
          <input
            className="w-full text-xs border rounded-md px-2 py-1.5 bg-white"
            placeholder="Where was this fingerprint independently obtained? (required)"
            value={sourceNote}
            onChange={(e) => setSourceNote(e.target.value)}
            data-testid="host-key-source-note-input"
          />
          <Button size="sm" onClick={handleVerify} disabled={busy !== '' || !fpInput.trim() || !sourceNote.trim()} data-testid="host-key-verify-btn">
            {busy === 'verify' ? 'Verifying…' : 'Verify & pin'}
          </Button>
        </div>
      )}

      {preflight && (
        <div className="space-y-1.5 p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="host-key-preflight-results">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-700">Preflight — {preflight.ran_at}</span>
            <Badge className={preflight.overall === 'ready' ? STATUS_BADGE.verified_pinned.cls : STATUS_BADGE.change_detected.cls}>
              {preflight.overall.toUpperCase()}
            </Badge>
          </div>
          {preflight.steps.map((s) => (
            <div key={s.step} className="flex items-start gap-2 text-[11px]">
              <span className={`shrink-0 font-semibold w-40 ${s.status === 'pass' ? 'text-emerald-700' : s.status === 'fail' ? 'text-red-700' : 'text-amber-700'}`}>
                {s.step} — {s.status}
              </span>
              <span className="text-slate-600 break-all">{typeof s.detail === 'string' ? s.detail : JSON.stringify(s.detail)}</span>
            </div>
          ))}
          <p className="text-[10px] text-slate-400">{preflight.note}</p>
        </div>
      )}
    </div>
  );
};

export default HostKeyPinningCard;
